import { Controller } from "@hotwired/stimulus"
import airports from "../data/airports.json"

export default class extends Controller {
  static targets = ["input", "list", "icao", "gravity", "gravitySource", "latitude"]

  connect() {
    this.selectedIndex = -1
    this.listTarget.id ||= `airport-results-${Math.random().toString(36).slice(2)}`
    this.listTarget.setAttribute("role", "listbox")
    this.inputTarget.setAttribute("role", "combobox")
    this.inputTarget.setAttribute("aria-autocomplete", "list")
    this.inputTarget.setAttribute("aria-controls", this.listTarget.id)
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  search() {
    const query = this.inputTarget.value.trim().toLowerCase()
    if (query.length < 2) {
      this.hide()
      return
    }

    const results = airports.filter(a =>
      a.city.toLowerCase().includes(query) ||
      a.icao.toLowerCase().includes(query) ||
      a.iata.toLowerCase().includes(query) ||
      a.name.toLowerCase().includes(query)
    ).slice(0, 8)

    this.selectedIndex = -1
    this.showResults(results)
  }

  showResults(results) {
    if (results.length === 0) {
      this.hide()
      return
    }

    this.listTarget.innerHTML = results.map((a, i) =>
      `<button type="button" class="airport-option" role="option" id="${this.listTarget.id}-option-${i}" aria-selected="false" data-index="${i}" data-icao="${a.icao}" data-lat="${a.lat || ''}" data-city="${a.city}" data-action="click->airport-search#pick">
        <span class="airport-code">${a.icao}</span>
        <span class="airport-detail">
          <span class="airport-name">${a.name}</span>
          <span class="airport-city">${a.city}, ${a.country} &middot; ${a.iata}</span>
        </span>
      </button>`
    ).join("")

    this.listTarget.classList.add("active")
    this.inputTarget.setAttribute("aria-expanded", "true")
  }

  pick(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const icao = btn.dataset.icao
    const lat = parseFloat(btn.dataset.lat)
    const city = btn.dataset.city
    this.selectAirport(icao, lat, city)
  }

  selectAirport(icao, lat, city) {
    // If there's a separate icao target, store ICAO there and show city in input
    if (this.hasIcaoTarget) {
      this.icaoTarget.value = icao
      this.inputTarget.value = city || icao
    } else {
      this.inputTarget.value = icao
    }
    this.hide()
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (!isNaN(lat)) {
      if (this.hasLatitudeTarget) this.latitudeTarget.value = lat
      const g = this.computeGravity(lat)
      if (this.hasGravityTarget) this.gravityTarget.value = g
      if (this.hasGravitySourceTarget) this.gravitySourceTarget.value = `WGS-84 formula (${icao}, lat ${lat.toFixed(2)}°)`
    }
  }

  // International Gravity Formula (GRS80 / WGS-84)
  // g(φ) = 9.780327 × (1 + 0.0053024·sin²φ − 0.0000058·sin²2φ)
  computeGravity(latDeg) {
    const phi = latDeg * Math.PI / 180
    const sinPhi = Math.sin(phi)
    const sin2Phi = Math.sin(2 * phi)
    const g = 9.780327 * (1 + 0.0053024 * sinPhi * sinPhi - 0.0000058 * sin2Phi * sin2Phi)
    return g.toFixed(5)
  }

  navigate(event) {
    const options = this.listTarget.querySelectorAll(".airport-option")
    if (!options.length || !this.listTarget.classList.contains("active")) return

    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.selectedIndex = Math.min(this.selectedIndex + 1, options.length - 1)
      this.highlightOption(options)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
      this.highlightOption(options)
    } else if (event.key === "Enter" && this.selectedIndex >= 0) {
      event.preventDefault()
      const opt = options[this.selectedIndex]
      this.selectAirport(opt.dataset.icao, parseFloat(opt.dataset.lat), opt.dataset.city)
    } else if (event.key === "Escape") {
      this.hide()
    }
  }

  highlightOption(options) {
    options.forEach((o, i) => {
      const selected = i === this.selectedIndex
      o.classList.toggle("highlighted", selected)
      o.setAttribute("aria-selected", selected.toString())
    })
    if (options[this.selectedIndex]) {
      this.inputTarget.setAttribute("aria-activedescendant", options[this.selectedIndex].id)
      options[this.selectedIndex].scrollIntoView({ block: "nearest" })
    }
  }

  hide() {
    this.listTarget.classList.remove("active")
    this.listTarget.innerHTML = ""
    this.selectedIndex = -1
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }
}
